import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import {
  type AgentOrchestratorPlanStep,
  type AgentOrchestratorStepResult,
  type AgentToolCall,
  type ChatAttachmentErrorAction,
  type ChatAttachmentErrorCode,
  type OntologyAgentEvent,
  type OrchestratedStepKind,
  stripAnswerEnvelope
} from '@atbash-labs/ontology-agent-wire'

import type { ExplicitModelEffort } from '../api/model-policy/types'
import {
  getChatComposerModeForConversation,
  type ChatComposerMode
} from '../agents/orchestrator-ui-persistence'

/**
 * Tool call that's currently in flight or has just completed inside one of
 * the orchestrator's plan steps. Subagent-level events (`tool_call_*`) carry
 * `parentStepIndex` set to the plan step that owns them — that's the field
 * the UI uses to attribute the tool call to its row.
 */
export interface LiveOrchestratorToolCall extends AgentToolCall {
  /** 0-based plan step index — read from the inbound event's `parentStepIndex`. */
  parentStepIndex: number
  /** Actual subagent kind for nested calls; absent on older emitters. */
  agentKind?: string
  /** `null` when the inner subagent didn't tag the call with its own step (orchestrator-level tools). */
  innerStepIndex: number | null
  status: 'running' | 'completed'
  /**
   * Shared by tools that started while a peer on this plan step was still
   * running — the parallel execution group. Assigned by the SSE router.
   */
  batchId?: string
  /** Executor wall-clock time. Set on `tool_call_complete`; absent while running. */
  durationMs?: number
}

/**
 * One unit in a subagent step's inline live-transcript: a streamed prose
 * chunk emitted between tool calls. Ephemeral — never persisted, never
 * replayed for past turns.
 *
 * Modeled as a union so adding richer segment kinds (e.g. inline tool-call
 * pills, citation references) is additive without a schema migration.
 */
export type LiveSegment = { kind: 'progress'; text: string }

/**
 * One in-flight `wait_for_ingest_job` tool call. Drives the chat-level
 * "Ingesting…" status indicator with a live elapsed-time counter — the
 * ingest agent now blocks server-side on a completion callback instead of
 * burning LLM turns polling, so the UI surfaces the wait time directly.
 *
 * Keyed by tool-use id so multiple concurrent waits (parallel tool use)
 * can coexist; the chat status line shows the earliest `startedAtMs` so
 * the user sees the longest-running ingest's elapsed time.
 */
export interface InFlightIngestWait {
  /** Job UUID the agent is waiting on (from the tool input). */
  jobId: string
  /** ISO-derived ms since epoch — drives the "(0:42)" elapsed counter. */
  startedAtMs: number
}

/**
 * In-flight cypher-write proposal the user must accept or reject in the
 * sidebar (Cursor-style). The agent's `propose_cypher_writes` tool now
 * returns synchronously after persisting the row (async-suspend); the
 * card stays visible until the user clicks Apply or Reject and the
 * matching apply/reject mutation resolves successfully.
 *
 * Lives on `TabState` (NOT `TabRunState`) so cards survive the
 * orchestrator stream finishing — the propose tool now returns
 * synchronously after persisting the rows server-side, the run wraps
 * up, and the user can act on the cards in their own time. Cleared
 * when the synthetic follow-up turn for the plan dispatches or when
 * the tab itself is closed.
 *
 * Keyed by tool-use id (the same id the proxy persisted on the
 * `agent_writes` row); the api-service apply/reject endpoint is
 * `/agent-writes/by-tool-use/:toolUseId/{apply,reject}`.
 *
 * For multi-statement batches, each statement is materialized as its own
 * entry so the user can accept/reject rows independently — the derived
 * tool-use ids follow the server convention `<parentToolUseId>#<index>`
 * (index 0 reuses the parent id).
 *
 * `planId` / `writeId` are populated from the propose tool's
 * `tool_call_complete` output (or via hydrate-on-load) and drive the
 * per-plan completion check that fires the synthetic follow-up agent
 * turn once every row in a plan has been Applied or Rejected.
 *
 * `terminalState` records the per-row outcome locally — set when the
 * Apply / Reject mutation resolves. Plan-resume waits on every row
 * sharing a `planId` reaching a terminal state before firing the
 * synthetic follow-up turn. Once the resume turn POST returns the row
 * is dropped from the map.
 */
export type AgentActionTerminalState =
  | { kind: 'applied' }
  | { kind: 'rejected' }
  | { kind: 'apply_failed'; error: string }

/** @deprecated Alias retained for cypher-write call sites; use `AgentActionTerminalState`. */
export type CypherWriteTerminalState = AgentActionTerminalState

/**
 * Fields every agent-action approval proposal shares, regardless of the
 * underlying capability (cypher write, and future kinds). The generic
 * approval panel, the per-plan resume machinery, and the SSE routing all
 * read off these — capability-specific payload lives in the discriminated
 * members of `InFlightAgentActionProposal` below.
 *
 * `kind` selects the registered lifecycle + view capability. `summary`,
 * batch metadata, `planId`, and `terminalState` drive the shell; the
 * capability owns everything else.
 */
export interface BaseAgentActionProposal {
  /** Discriminator selecting the registered capability (view + lifecycle). */
  kind: string
  summary: string
  startedAtMs: number
  batchParentId?: string
  batchIndex?: number
  batchSize?: number
  batchSummary?: string
  planId?: string
  terminalState?: AgentActionTerminalState
}

/**
 * Shared apply/reject meta for graph mutation cards (legacy cypher +
 * structured envelope). Counts feed the resume instruction; rejection
 * reason feeds the settled card copy.
 */
export interface GraphMutationAppliedCounts {
  nodesCreated?: number
  nodesModified?: number
  nodesDeleted?: number
  relationshipsCreated?: number
  relationshipsDeleted?: number
  totalOperations?: number
}

/**
 * Legacy Cypher-write payload — retained for pending `cypher_write` /
 * `node_evidence` rows created before the structured-envelope cutover.
 */
export interface CypherWriteDetail {
  cypher: string
  params: Record<string, unknown>
  changeKind: string
  writeId?: string
  appliedCounts?: GraphMutationAppliedCounts
  rejectionReason?: string
}

export type InFlightCypherWriteProposal = BaseAgentActionProposal & {
  kind: 'cypher_write'
} & CypherWriteDetail

/**
 * Structured graph-write envelope fragment persisted on
 * `params.envelope` for `actionKind: 'graph_write'`. Mirrors the
 * propose_cypher_writes tool input / write-core shape.
 */
export interface GraphWriteEnvelopeDetail {
  nodes?: Array<Record<string, unknown>>
  relationships?: Array<Record<string, unknown>>
  deletes?: Array<Record<string, unknown>>
  overwrites?: Array<Record<string, unknown>>
}

export interface GraphWriteDetail {
  envelope: GraphWriteEnvelopeDetail
  changeKind: string
  writeId?: string
  appliedCounts?: GraphMutationAppliedCounts
  rejectionReason?: string
}

export type InFlightGraphWriteProposal = BaseAgentActionProposal & {
  kind: 'graph_write'
} & GraphWriteDetail

export interface TagWriteDetail {
  tagId: string
  proposedValue: number | string | boolean
  reason: string
  expiresAt?: string
  revertCondition?: Record<string, unknown>
  target?: Record<string, unknown>
  currentValue?: unknown
  policySnapshot?: unknown
  trend?: unknown[]
  writeId?: string
  priorValue?: unknown
  reversibleEventId?: string
  rejectionReason?: string
}

export type InFlightTagWriteProposal = BaseAgentActionProposal & {
  kind: 'tag_write'
} & TagWriteDetail

export interface TagWritePolicyDetail {
  tagId: string
  registryId: string
  reason: string
  target?: Record<string, unknown>
  currentPolicy: Record<string, unknown>
  proposedPolicy: Record<string, unknown>
  writeId?: string
  appliedPolicy?: Record<string, unknown>
  rejectionReason?: string
}

export type InFlightTagWritePolicyProposal = BaseAgentActionProposal & {
  kind: 'tag_write_policy'
} & TagWritePolicyDetail

export type TagHistorizationOperation = 'enable' | 'disable' | 'change_source'

export interface TagHistorizationTag {
  nodeId: string
  tagPath: string
}

export interface TagHistorizationExcludedTag extends TagHistorizationTag {
  reasonCode: string
  reason: string
}

export interface TagHistorizationAppliedResult {
  registeredCount: number
  /** Registered, but acquisition is quarantined so they are not collecting yet. */
  quarantinedCount: number
  enabledCount: number
  disabledCount: number
  convertedCount: number
  /** Approved tags that already matched the requested state at Apply time. */
  unchangedCount: number
  failed: Array<{ tagPath: string; reason: string }>
}

/**
 * One history-collection change covering a whole selection. Registry-only
 * (not graph), so Apply is not branch-gated.
 */
export interface TagHistorizationDetail {
  operation: TagHistorizationOperation
  reason: string
  /** True on the optimistic card until the persisted row hydrates the buckets. */
  resolving?: boolean
  /** Permissions this exact proposal needs at Apply (they vary by bucket). */
  requiredPermissions: string[]
  toRegister: TagHistorizationTag[]
  toEnable: TagHistorizationTag[]
  toDisable: TagHistorizationTag[]
  /** change_source: every registered tag being converted. */
  convertTags: TagHistorizationTag[]
  registerDestination?: { transport: string; url: string }
  source?: { sourceUrl: string; scrapeMode: string }
  target?: {
    scrapeMode: string
    destinationUrl: string
    credentialPosture: string | null
  }
  unchangedCount: number
  excluded: TagHistorizationExcludedTag[]
  writeId?: string
  appliedResult?: TagHistorizationAppliedResult
  rejectionReason?: string
}

export type InFlightTagHistorizationProposal = BaseAgentActionProposal & {
  kind: 'tag_historization'
} & TagHistorizationDetail

/**
 * ServiceNow-incident-specific payload — the first NON-graph registered
 * agent-action capability. The `create_servicenow_incident` tool emits
 * these; the user approves/rejects each before the incident is opened
 * against ServiceNow.
 */
export interface ServicenowIncidentDetail {
  integrationId?: string
  shortDescription: string
  /** Optional create fields (description, impact, urgency, …) passed through. */
  fields: Record<string, string>
  writeId?: string
  /** For `applied` rows: the created incident identifiers the proxy returned. */
  createdIncident?: { sysId?: string; number?: string }
  /** For `rejected` rows: the reason text the user supplied (or default). */
  rejectionReason?: string
}

export type InFlightServicenowIncidentProposal = BaseAgentActionProposal & {
  kind: 'servicenow_incident'
} & ServicenowIncidentDetail

/**
 * Create-branch payload — the approval-gated path out of a protected
 * branch. The `propose_create_branch` tool emits these; on approve the
 * branch is created off the conversation's active branch (main when none)
 * and the tab switches onto it.
 */
export interface CreateBranchDetail {
  /** Proposed branch name (the apply may suffix it on collision). */
  branchName: string
  /** Agent-supplied one-sentence reason shown on the card. */
  reason?: string
  /** Name of the branch being forked from; absent until hydrated (or main). */
  sourceName?: string
  writeId?: string
  /** For `applied` rows: the created branch the apply returned. */
  createdBranch?: { id?: string; name?: string }
  /** For `rejected` rows: the reason text the user supplied (or default). */
  rejectionReason?: string
}

export type InFlightCreateBranchProposal = BaseAgentActionProposal & {
  kind: 'create_branch'
} & CreateBranchDetail

/**
 * Create a builder-authored DB writeback tool. `add_db_write_tool` emits
 * these; Apply POSTs the registry row (and optionally binds it to a
 * custom-agent config in the same approval).
 */
export interface CreateWritebackToolDetail {
  name: string
  /** Registry tool kind — DB only for now. Distinct from the action `kind`. */
  toolKind: 'db'
  parametrizedQuery: string
  params: string[]
  connectionId: string
  reason: string
  /** When set, Apply also binds the new tool to this custom-agent config. */
  configId?: string
  /** Display name of that config, looked up at propose time. */
  configName?: string
  writeId?: string
  /** For `applied` rows: the registry tool the apply returned. */
  createdTool?: { id?: string; name?: string }
  /** For `applied` rows: true when Apply also bound the new tool. */
  bound?: boolean
  /** For `rejected` rows: the reason text the user supplied (or default). */
  rejectionReason?: string
}

export type InFlightCreateWritebackToolProposal = BaseAgentActionProposal & {
  kind: 'create_writeback_tool'
} & CreateWritebackToolDetail

/**
 * Bind an existing writeback tool to a custom agent. `use_writeback_tool`
 * emits these; Apply POSTs the binding.
 */
export interface BindWritebackToolDetail {
  toolId: string
  configId: string
  /** Display name of the target custom agent, looked up at propose time. */
  configName?: string
  reason: string
  /** Registry slug from GET /writeback/tools/{id} at propose time. */
  name?: string
  writeId?: string
  /** For `applied` rows: the bound tool the apply returned. */
  boundTool?: { id?: string; name?: string }
  /** For `rejected` rows: the reason text the user supplied (or default). */
  rejectionReason?: string
}

export type InFlightBindWritebackToolProposal = BaseAgentActionProposal & {
  kind: 'bind_writeback_tool'
} & BindWritebackToolDetail

/** Follow-up park of a this-agent config PATCH. Apply later on the run page. */
export interface CustomAgentConfigUpdateDetail {
  configId: string
  summary: string
  prompt?: string
  triggers?: unknown[]
  triggerEvents?: string[]
  cron?: string
  triggerCooldownMs?: number
  analysisWindowMs?: number
  writeId?: string
}

export type InFlightCustomAgentConfigUpdateProposal = BaseAgentActionProposal & {
  kind: 'update_custom_agent_config'
} & CustomAgentConfigUpdateDetail

export type InFlightCustomAgentRunProposal = BaseAgentActionProposal & {
  kind: 'custom_agent_run'
  name: string
  reason: string
  parameters?: Record<string, unknown>
  instruction?: string
  parentRunId?: string
  itemCount?: number
  accepted?: boolean
  writeId?: string
}

export interface ConditionVocabularyDetail {
  conditionId: string
  name: string
  conditionKind: string
  perceivableBy: string[]
  equipmentType: string
  howToCheck: string
  writeId?: string
}

export type InFlightConditionVocabularyProposal = BaseAgentActionProposal & {
  kind: 'condition_vocabulary'
} & ConditionVocabularyDetail

export interface IndicatesDetail {
  instrumentId: string
  observableId: string
  display?: string
  rangeLow?: number
  rangeHigh?: number
  source?: string
  writeId?: string
}

export type InFlightIndicatesProposal = BaseAgentActionProposal & {
  kind: 'indicates'
} & IndicatesDetail

/**
 * Discriminated union over every registered agent-action proposal kind.
 * Add a member here when a new capability is registered; the generic
 * panel/slice/resume code switches on `kind` via the capability
 * registries rather than hard-coding any single kind.
 */
export type InFlightAgentActionProposal =
  | InFlightCypherWriteProposal
  | InFlightGraphWriteProposal
  | InFlightTagWriteProposal
  | InFlightTagWritePolicyProposal
  | InFlightTagHistorizationProposal
  | InFlightServicenowIncidentProposal
  | InFlightCreateBranchProposal
  | InFlightCreateWritebackToolProposal
  | InFlightBindWritebackToolProposal
  | InFlightCustomAgentConfigUpdateProposal
  | InFlightCustomAgentRunProposal
  | InFlightConditionVocabularyProposal
  | InFlightIndicatesProposal

/**
 * Conversation list-row view. Mirrors the proxy-server's
 * `ConversationSummary` JSON shape — wire keys stay snake_case so we don't
 * need a separate normalization layer.
 */
export interface ConversationSummary {
  id: string
  title: string
  /** Access scope governing who can view or contribute to this conversation. */
  accessScopeId?: string
  /** Request-relative capability; only the conversation owner can contribute. */
  canContribute?: boolean
  turn_count: number
  created_at: string
  updated_at: string
  /** Present when the proxy returns it; ingest-query Ask lanes use `ingest_query`. */
  origin?: string
}

/**
 * One persisted attachment row for a conversation. Mirrors the
 * proxy-server's `AttachmentSummary` shape (snake_case wire format) — the
 * api-service forwards the proxy response unchanged so the slice doesn't
 * need a normalization layer. The chat sidebar renders these in a
 * "Documents in this conversation" panel so the user can see every file
 * the orchestrator has been handed in this thread.
 */
export interface ConversationAttachment {
  id: string
  conversation_id: string
  /** Orchestrator run that introduced the file, when known. */
  run_id?: string | null
  filename: string
  mime_type: string
  size_bytes: number
  metadata?: {
    source?: 'docs_hub' | 'ingest_corpus'
    doc_path?: string
    job_id?: string
    page?: number
    bbox?: number[]
    caption?: string
    created_by?: 'agent'
  }
  created_at: string
}

/**
 * Lifecycle of a browser file staged in the composer. `accepted` is not a
 * status here — acceptance removes the item from staged state entirely.
 */
export type StagedAttachmentStatus = 'staged' | 'sending' | 'failed'

/**
 * Serializable composer-file metadata. Browser `File` bytes live in the
 * session-memory registry, keyed by `clientId`.
 */
export interface StagedAttachment {
  clientId: string
  filename: string
  mimeType: string
  sizeBytes: number
  lastModified: number
  status: StagedAttachmentStatus
  error: string | null
  errorCode: ChatAttachmentErrorCode | null
  retryable: boolean
  errorAction?: ChatAttachmentErrorAction
}

/** User-safe copy shown on a failed staged-file group. */
export const STAGED_ATTACHMENT_SEND_ERROR = "Couldn't send the attached files. Try again."

/** A user turn waiting in the server-side Redis FIFO for this conversation. */
export interface QueuedOrchestratorMessage {
  id: string
  instruction: string
  queued_at: string
}

/** Lifecycle status shared by every ConversationRun. */
export type RunStatus = 'running' | 'completed' | 'failed' | 'aborted'

/**
 * `agent_kind` from `agent_runs`. Top-level runs are `agent_orchestrator` (the
 * planner loop) or `unstructured_ingest`; every other value is a subagent
 * dispatch nested under a parent run.
 *
 * This is a plain `string` because the proxy validates the kind structurally
 * (top-level closed, nested open), so a new orchestrator subagent flows through
 * without a UI change — `agentLabel(kind)` already renders any unknown kind by
 * title-casing it. The dispatchable catalog lives in
 * `@atbash-labs/ontology-agent-wire` `OrchestratedAgentKind`, not here.
 */
export type AgentKind = string

/**
 * One row from `agent_messages`. `content` is the raw Anthropic
 * `ContentBlockParam[]` preserved as JSONB (text + tool_use + tool_result
 * blocks).
 */
export interface ConversationMessageRow {
  id: string
  run_id: string
  sequence: number
  role: 'user' | 'assistant'
  content: unknown
  created_at: string
}

/**
 * The orchestrator's structured payload, persisted as `raw_response` on the
 * top-level orchestrator run. Subagent runs carry their loop's own typed
 * response shape (PipelineAnswer / RcaBuilderResponse / etc.) — kept as
 * `unknown` here since the UI only inspects orchestrator runs structurally.
 */
export interface OrchestratorRunPayload {
  plan: AgentOrchestratorPlanStep[]
  step_results: AgentOrchestratorStepResult[]
  final_summary: string
}

/**
 * One persisted run in a conversation. Mirrors the proxy-server's
 * `RunWithChildren` shape — top-level orchestrator run + its planner LLM
 * message stream + nested subagent dispatches (each with their own
 * messages, recursively).
 *
 * In v1 only the orchestrator goes one level deep, but the schema supports
 * arbitrary nesting and this type is recursive so render code walks
 * `children` without caring about depth.
 */
export interface ConversationRun {
  id: string
  conversation_id: string
  parent_run_id?: string | null
  agent_kind: AgentKind
  /** Per-conversation 1-based sequence for orchestrator runs; per-parent 0-based step_index for subagent runs. */
  position: number
  prompt: string
  status: RunStatus
  raw_response?: unknown
  error?: string
  started_at: string
  finished_at?: string
  messages: ConversationMessageRow[]
  children: ConversationRun[]
  /**
   * Subagent-narration progress segments captured from the live run that
   * just produced this turn, keyed by plan step index. In-memory carry-
   * forward from `tab.run.liveSegmentsByStep` at `appendTabTurn` time —
   * lets the chat re-render committed CoT when the user scrolls back to a
   * past turn in the current session. Absent for turns loaded from the
   * server (refresh-loss) and for past turns that ran before this field
   * existed.
   */
  progressByStep?: Record<number, LiveSegment[]>
  /**
   * Orchestrator inter-step narration — the planner LLM's connective
   * prose between subagent dispatches, keyed by the upcoming step's
   * index. Same in-memory carry-forward shape as `progressByStep`.
   */
  interStepTextByStep?: Record<number, string>
  /**
   * Provider/model hop captured during this turn. In-memory carry from
   * `tab.run.modelFallback` at `appendTabTurn` time — lost on refresh.
   */
  modelFallback?: ModelFallbackInfo
  /**
   * Context compaction captured during this turn (earlier turns were
   * summarized by the model provider or the proxy). In-memory carry from
   * `tab.run.contextCompacted` at `appendTabTurn` time — lost on refresh.
   * The debug task summary recovers the same fact from persisted assistant
   * `compaction` blocks, which survive refresh.
   */
  contextCompacted?: ContextCompactedInfo
}

/** Payload of the wire `context_compacted` event, minus its envelope fields. */
export type ContextCompactedInfo = Omit<
  Extract<OntologyAgentEvent, { type: 'context_compacted' }>,
  'type' | 'pipelineId' | 'timestamp'
>

export interface ModelFallbackInfo {
  requestedProvider: string
  requestedModel: string
  provider: string
  model: string
  reason: 'overloaded' | 'credentials' | 'unavailable'
  notify: boolean
}

export interface ConversationWithRuns extends ConversationSummary {
  runs: ConversationRun[]
}

/**
 * Per-tab in-flight orchestrator run. `null` on `TabState.run` when the tab
 * is idle. Every field here used to live as a singleton on the slice — the
 * multi-tab rewrite (AXI-3530) moved them into this struct so two tabs can
 * each carry their own in-flight stream without stepping on each other.
 *
 * Lifecycle: `startTabRun` builds a fresh instance via `makeStartedRun`;
 * SSE event reducers mutate fields in place; `finishTabRun` clears the
 * whole struct back to `null` after the run is promoted to `tab.turns`
 * (whether it completed or the user stopped it). `failTabRun` keeps the
 * struct around so the error stays visible until the next run.
 */
export interface TabRunState {
  /** Which long-running SSE route owns this live tab run. */
  runKind: 'orchestrator' | 'factory_monitoring'
  /** The instruction the planner is currently running on. */
  instruction: string
  /** Plan emitted by `agent_orchestrator_plan_created`; `null` until the orchestrator commits to one. */
  plan: AgentOrchestratorPlanStep[] | null
  /** Per-step results keyed by `stepIndex` — populated by `agent_orchestrator_step_*` events. */
  stepResults: Record<number, AgentOrchestratorStepResult>
  liveToolCalls: LiveOrchestratorToolCall[]
  /**
   * Per-step inline transcript: committed progress segments (one paragraph
   * per finished progress note). Driven by
   * `agent_orchestrator_subagent_progress_commit` events; render-only,
   * never persisted, never used as the step's canonical summary.
   */
  liveSegmentsByStep: Record<number, LiveSegment[]>
  /**
   * Per-step in-flight pending text — the current text content block being
   * streamed by the subagent's LLM, token-by-token via
   * `agent_orchestrator_subagent_progress_delta` events. Promoted into a
   * committed segment in `liveSegmentsByStep` when a non-finalize tool_use
   * lands (`..._progress_commit`), or dropped when `finalize_answer` lands
   * (`..._progress_discard`). Rendered with subtle in-flight styling so
   * the discard-on-finalize transition is less jarring.
   */
  pendingProgressByStep: Record<number, string>
  /**
   * Per-step orchestrator inter-step narration text — the planner LLM's
   * connective prose between subagents, keyed by the upcoming step's
   * index. Driven by `agent_orchestrator_inter_step_delta` events. The
   * UI renders this above the matching step section in the live
   * transcript so each subagent dispatch has a brief intro from the
   * orchestrator. Cleared with the rest of the run on `finishTabRun`.
   */
  interStepTextByStep: Record<number, string>
  /**
   * Latest provider/model hop for this live run. Last `model_fallback`
   * event wins. The chat banner only renders when `notify` is true.
   */
  modelFallback: ModelFallbackInfo | null
  /** Latest `context_compacted` event for this live run, if any. */
  contextCompacted: ContextCompactedInfo | null
  /**
   * Map of in-flight `wait_for_ingest_job` tool calls, keyed by tool-use id.
   * Populated by the SSE handler on `tool_call_start` for the ingest wait
   * tool and cleared on the matching `tool_call_complete` (or when the
   * stream lifecycle resets). Empty `{}` when no ingest wait is active.
   */
  inFlightIngestWaits: Record<string, InFlightIngestWait>
  /** Synthesized wrap-up; populated by `agent_orchestrator_finalized` events while the stream is still open. */
  liveFinalSummary: string | null
  /**
   * `true` when `liveFinalSummary` currently holds the client-promoted
   * streamed answer from `discardSubagentProgressPending` (fast-path
   * finalize) rather than server-emitted summary text. Lets
   * `appendTabFinalSummaryDelta` REPLACE the promoted text when a planner
   * synthesis turn starts streaming its own wrap-up — appending onto the
   * promoted answer duplicated it — while `setTabFinalSummary` clears the
   * flag when the canonical summary lands.
   */
  liveFinalSummaryPromoted: boolean
  streamError: string | null
  /**
   * `true` from the moment the user clicks Stop until the SSE connection
   * closes (success, failure, or stop). The Stop button renders
   * "Cancelling…" + disabled while this is set, so a click during the
   * round-trip looks responsive instead of leaving the user staring at a
   * Stop button that did nothing. Cleared by the same reducers that
   * unwind other live-streaming fields.
   */
  cancelRequested: boolean
}

/**
 * Full state for one open conversation tab. Keyed by `conversationId` in
 * `AgentOrchestratorState.tabs`. Holds both the persisted transcript /
 * attachment list and the live-streaming `run` so tab-switching is a
 * single Redux read with no on-demand fetch.
 *
 * When the user closes a tab via the X button, the slice removes its
 * entry — the next open of the same conversation re-fetches its
 * transcript. This is the GC strategy: bounded memory in exchange for a
 * one-time refetch on reopen.
 */
export interface TabState {
  /** Defaults false until the server capability for this conversation is loaded. */
  canContribute: boolean
  /**
   * Per-tab composer text. Preserved across tab switches so typing a draft
   * in one tab and clicking another doesn't silently destroy it (decision
   * #4 in the AXI-3530 plan).
   */
  draft: string
  /**
   * Top-level orchestrator runs ("turns") for this conversation, in
   * `position ASC` order. Each run carries its planner-LLM message stream
   * plus nested subagent runs under `children`. Empty for a fresh
   * conversation; populated by `setTabTurns` after the conversation
   * loads, and appended via `appendTabTurn` when the in-flight stream
   * completes.
   */
  turns: ConversationRun[]
  /**
   * True once `setTabTurns` has resolved at least once for this tab.
   * Drives the "loading…" vs "no turns yet" empty-state branching in the
   * chat pane.
   */
  turnsLoaded: boolean
  /**
   * Files uploaded across the lifetime of this conversation, in
   * chronological (oldest-first) order. Sourced from the proxy's
   * per-conversation attachment registry — the same list the orchestrator
   * route rehydrates each turn.
   */
  attachments: ConversationAttachment[]
  /**
   * True after the first attachment-list fetch resolves for this tab.
   * Drives the "Loading…" placeholder vs. the empty-state copy in the
   * documents panel.
   */
  attachmentsLoaded: boolean
  /**
   * Optimistic, client-only preview rows for files the user has just
   * attached to the in-flight turn. Surfaced in the documents panel
   * under an "Uploading with this turn…" group so the user sees what
   * they uploaded immediately — without this, the panel would be blind
   * to brand-new uploads until the orchestrator stream completed and
   * the proxy-side attachment registry was re-fetched (which is
   * especially confusing on the very first turn of a fresh
   * conversation, when the panel is otherwise empty).
   *
   * Synthesized in `useOrchestratorChatController.handleSubmit` from
   * the `File` objects the user picked, with synthetic ids prefixed
   * `pending-` so they can never collide with a proxy-assigned UUID.
   * Cleared on stream finish / failure / abort by the matching
   * `finishTabRun` / `failTabRun` reducer — by the time
   * the user sees a finished turn, the same files are back in the
   * canonical `attachments` list via the post-stream refetch.
   */
  liveTurnAttachmentPreviews: ConversationAttachment[]
  /**
   * Unsent / retryable composer files owned by this conversation. Bytes
   * stay in the session-memory registry; this list is metadata only.
   * Distinct from `attachments` (durable server list) and
   * `liveTurnAttachmentPreviews` (documents-panel presentation).
   */
  stagedAttachments: StagedAttachment[]
  /**
   * In-flight agent-action approval proposals the user must accept or
   * reject in this tab's conversation (cypher writes today; other
   * permission-gated AI actions in future). Keyed by `tool_use_id` (or
   * `<tool_use_id>#<index>` for multi-statement batches). Survives the
   * orchestrator stream finishing — the propose tool uses async-suspend
   * so the run wraps up and the user can decide on each card in their
   * own time. Cleared by the per-plan synthetic-resume dispatch or when
   * the tab itself is closed.
   */
  inFlightAgentActions: Record<string, InFlightAgentActionProposal>
  /** Background custom-agent runs started by this conversation, keyed by run id. */
  customAgentRuns: Record<string, ChatCustomAgentRun>
  /** Debug bulk batches started by this conversation, keyed by batch id. */
  customAgentBulkBatches: Record<string, ChatCustomAgentBulkBatch>
  /** User turns waiting for the current conversation lease to be released. */
  queuedMessages: QueuedOrchestratorMessage[]
  /**
   * In-flight orchestrator run for this tab; `null` when the tab is idle.
   * Multiple tabs can each hold a non-null `run` simultaneously — the
   * server-side registry in `agent-orchestrator-registry.ts` is already
   * keyed by `conversationId` and supports parallel sessions.
   */
  run: TabRunState | null
  /**
   * Per-conversation model override picked in the composer's model selector.
   * `null` = "Auto" (the org's policy-resolved model). Sent with every turn
   * of this conversation as `model_provider`/`model_id`; never written to the
   * org-wide policy. UI-state only — closing the tab resets the conversation
   * to Auto on reopen.
   */
  modelSelection: OrchestratorModelSelection | null
  /**
   * Per-conversation Ask/Agent composer mode. Unlike `modelSelection`, this
   * is restored on reopen from localStorage — resetting it would silently
   * run an Agent thread as query-only. `null` = not yet assigned; the
   * controller falls back to Ask.
   */
  composerMode: ChatComposerMode | null
  /**
   * Per-conversation thinking-effort override from the composer. `null` on
   * Auto (the request path sends medium) or when a pinned model should
   * inherit the org / provider default. Sent as `effort` when a pinned
   * model has an explicit pick; never written to org policy.
   */
  effortSelection: OrchestratorEffortSelection | null
}

export interface ChatCustomAgentRun {
  runId: string
  configId?: string
  name: string
  parameters?: Record<string, unknown>
  kind?: 'chat' | 'follow_up'
  parentRunId?: string
}

export interface ChatCustomAgentBulkBatch {
  batchId: string
  name: string
  itemCount: number
}

/** A provider/model pair chosen in the chat composer's model selector. */
export interface OrchestratorModelSelection {
  provider: string
  model: string
}

/** Thinking effort chosen in the chat composer (org default when unset). */
export type OrchestratorEffortSelection = ExplicitModelEffort

export interface AgentOrchestratorState {
  // ── Conversation list rail ───────────────────────────────────────────
  conversations: ConversationSummary[]
  /** True after the first successful list fetch — drives the "loading…" placeholder. */
  conversationsLoaded: boolean

  // ── Open tabs (AXI-3530) ──────────────────────────────────────────────
  /**
   * Ordered list of conversation ids currently open in the tab strip.
   * Persisted to localStorage so refresh restores the working set the user
   * assembled. Always a subset of `conversations`; tabs whose underlying
   * conversation was deleted server-side are pruned on rehydrate.
   */
  openTabs: string[]
  /**
   * Which tab the chat pane is currently rendering. `null` focuses the
   * pending new-chat tab (empty composer bound to `pendingDraft`); on
   * first submit from that state the orchestrator thunk pre-creates the
   * conversation, opens a real tab, and flips `activeTabId` to its id.
   */
  activeTabId: string | null
  /**
   * Per-conversation tab state, keyed by `conversationId`. Only contains
   * entries for tabs currently in `openTabs` — `closeTab` drops the entry,
   * and the next open of the same conversation re-fetches its transcript.
   */
  tabs: Record<string, TabState>

  /**
   * Composer text for the pending new-chat tab (rendered when
   * `activeTabId === null`). Lives outside `tabs` because it has no
   * `conversationId` yet — only one pending new-chat state exists at a
   * time (ChatGPT-style). The text is preserved across switches to real
   * tabs so clicking "+ New", typing, switching to a real tab, and coming
   * back doesn't lose work.
   */
  pendingDraft: string

  /**
   * Model selection for the pending new-chat composer (`activeTabId ===
   * null`). Carried onto the real tab when the first submit pre-creates the
   * conversation, then cleared. `null` = Auto.
   */
  pendingModelSelection: OrchestratorModelSelection | null

  /**
   * Effort selection for the pending new-chat composer. Carried onto the
   * real tab on first submit, then cleared. `null` = organization default.
   */
  pendingEffortSelection: OrchestratorEffortSelection | null

  /**
   * Ask/Agent mode for the pending new-chat composer. Carried onto the
   * real tab on first submit. `null` = Ask.
   */
  pendingComposerMode: ChatComposerMode | null

  /**
   * Unsent / retryable composer files for the pending new-chat tab
   * (`activeTabId === null`). Moved onto the created tab's
   * `stagedAttachments` after conversation creation without changing
   * client IDs.
   */
  pendingStagedAttachments: StagedAttachment[]
}

function makeEmptyTabState(): TabState {
  return {
    canContribute: false,
    draft: '',
    turns: [],
    turnsLoaded: false,
    attachments: [],
    attachmentsLoaded: false,
    liveTurnAttachmentPreviews: [],
    stagedAttachments: [],
    inFlightAgentActions: {},
    customAgentRuns: {},
    customAgentBulkBatches: {},
    queuedMessages: [],
    run: null,
    modelSelection: null,
    effortSelection: null,
    composerMode: null
  }
}

function makeStartedRun(
  instruction: string,
  runKind: TabRunState['runKind'] = 'orchestrator'
): TabRunState {
  return {
    runKind,
    instruction,
    plan: null,
    stepResults: {},
    liveToolCalls: [],
    liveSegmentsByStep: {},
    pendingProgressByStep: {},
    interStepTextByStep: {},
    modelFallback: null,
    contextCompacted: null,
    inFlightIngestWaits: {},
    liveFinalSummary: null,
    liveFinalSummaryPromoted: false,
    streamError: null,
    cancelRequested: false
  }
}

/**
 * `true` when the run's plan is the single-direct-query fast path and the
 * given step is that one step — mirrors `singleDirectQueryStepIndex` in
 * `ChatTurn.tsx` (keep the two in sync). Only in this shape does the chat
 * answer body render the streamed pending text, so only here may
 * `discardSubagentProgressPending` promote it into `liveFinalSummary`.
 */
function applyStagedAttachmentStatus(
  items: StagedAttachment[],
  clientIds: string[],
  status: StagedAttachmentStatus,
  failure: {
    error: string | null
    errorCode: ChatAttachmentErrorCode | null
    retryable: boolean
    errorAction?: ChatAttachmentErrorAction
  }
): StagedAttachment[] {
  if (clientIds.length === 0) return items
  const idSet = new Set(clientIds)
  return items.map((item) =>
    idSet.has(item.clientId)
      ? {
          ...item,
          status,
          error: failure.error,
          errorCode: failure.errorCode,
          retryable: failure.retryable,
          ...(failure.errorAction === undefined
            ? { errorAction: undefined }
            : { errorAction: failure.errorAction })
        }
      : item
  )
}

function removeStagedAttachmentsById(
  items: StagedAttachment[],
  clientIds: string[]
): StagedAttachment[] {
  if (clientIds.length === 0) return items
  const idSet = new Set(clientIds)
  return items.filter((item) => !idSet.has(item.clientId))
}

function isSingleDirectQueryStep(
  plan: AgentOrchestratorPlanStep[] | null,
  stepIndex: number
): boolean {
  if (plan === null || plan.length !== 1) return false
  const step = plan[0]
  if (step.index !== stepIndex) return false
  return step.intended_agent === 'query_agent' || step.intended_agent === 'ingest_query_agent'
}

/**
 * Carry forward in-memory CoT artifacts (progress segments + orchestrator
 * inter-step text) from the previous turns onto the incoming server-loaded
 * turns by array index. Required because the server doesn't (yet) persist
 * these — without this, every conversation refetch that lands after a
 * successful run would blow away the CoT that `appendTabTurn` just stamped
 * onto the synthetic turn.
 *
 * Server-provided fields always win when present, so durable persistence
 * (if it ever ships) takes precedence over the in-memory carry. When
 * index N has no previous and no incoming value, the turn is returned
 * unchanged (no spurious field churn for server-loaded history).
 */
function mergeInMemoryProgressByStep(
  previous: ConversationRun[],
  incoming: ConversationRun[]
): ConversationRun[] {
  if (previous.length === 0) return incoming
  return incoming.map((turn, i) => {
    const prev = previous[i]
    if (prev === undefined) return turn
    let merged: ConversationRun | null = null
    if (turn.progressByStep === undefined && prev.progressByStep !== undefined) {
      merged = { ...turn, progressByStep: prev.progressByStep }
    }
    if (turn.interStepTextByStep === undefined && prev.interStepTextByStep !== undefined) {
      merged = {
        ...(merged ?? turn),
        interStepTextByStep: prev.interStepTextByStep
      }
    }
    if (turn.modelFallback === undefined && prev.modelFallback !== undefined) {
      merged = {
        ...(merged ?? turn),
        modelFallback: prev.modelFallback
      }
    }
    if (turn.contextCompacted === undefined && prev.contextCompacted !== undefined) {
      merged = {
        ...(merged ?? turn),
        contextCompacted: prev.contextCompacted
      }
    }
    return merged ?? turn
  })
}

const initialState: AgentOrchestratorState = {
  conversations: [],
  conversationsLoaded: false,
  openTabs: [],
  activeTabId: null,
  tabs: {},
  pendingDraft: '',
  pendingModelSelection: null,
  pendingEffortSelection: null,
  pendingComposerMode: null,
  pendingStagedAttachments: []
}

const agentOrchestratorSlice = createSlice({
  name: 'agentOrchestrator',
  initialState,
  reducers: {
    // ── Pending new-chat composer ──────────────────────────────────────

    /** Set the composer text shown when `activeTabId === null`. */
    setPendingDraft(state, action: PayloadAction<string>) {
      state.pendingDraft = action.payload
    },

    /** Set the model selection for the pending new-chat composer. */
    setPendingModelSelection(state, action: PayloadAction<OrchestratorModelSelection | null>) {
      state.pendingModelSelection = action.payload
    },

    /** Set the effort selection for the pending new-chat composer. */
    setPendingEffortSelection(state, action: PayloadAction<OrchestratorEffortSelection | null>) {
      state.pendingEffortSelection = action.payload
    },

    /** Set the Ask/Agent mode for the pending new-chat composer. */
    setPendingComposerMode(state, action: PayloadAction<ChatComposerMode | null>) {
      state.pendingComposerMode = action.payload
    },

    /** Append staged-file metadata to the pending new-chat composer. */
    addPendingStagedAttachments(state, action: PayloadAction<StagedAttachment[]>) {
      if (action.payload.length === 0) return
      state.pendingStagedAttachments.push(...action.payload)
    },

    /** Drop pending staged-file metadata by client ID. */
    removePendingStagedAttachments(state, action: PayloadAction<{ clientIds: string[] }>) {
      state.pendingStagedAttachments = removeStagedAttachmentsById(
        state.pendingStagedAttachments,
        action.payload.clientIds
      )
    },

    /** Update status for a subset of pending staged files. */
    setPendingStagedAttachmentStatus(
      state,
      action: PayloadAction<{
        clientIds: string[]
        status: StagedAttachmentStatus
        error?: string | null
        errorCode?: ChatAttachmentErrorCode | null
        retryable?: boolean
        errorAction?: ChatAttachmentErrorAction
      }>
    ) {
      state.pendingStagedAttachments = applyStagedAttachmentStatus(
        state.pendingStagedAttachments,
        action.payload.clientIds,
        action.payload.status,
        action.payload.status === 'failed'
          ? {
              error: action.payload.error ?? null,
              errorCode: action.payload.errorCode ?? null,
              retryable: action.payload.retryable ?? true,
              errorAction: action.payload.errorAction ?? 'retry'
            }
          : { error: null, errorCode: null, retryable: false }
      )
    },

    /**
     * Move a submitted pending ID set onto a real tab without changing
     * client IDs or status. IDs that are not in pending are ignored so a
     * concurrently staged file cannot be claimed by the wrong request.
     */
    transferPendingStagedAttachments(
      state,
      action: PayloadAction<{ conversationId: string; clientIds: string[] }>
    ) {
      const { conversationId, clientIds } = action.payload
      if (clientIds.length === 0) return
      const tab = state.tabs[conversationId]
      if (tab === undefined) return
      const idSet = new Set(clientIds)
      const moving: StagedAttachment[] = []
      const remaining: StagedAttachment[] = []
      for (const item of state.pendingStagedAttachments) {
        if (idSet.has(item.clientId)) moving.push(item)
        else remaining.push(item)
      }
      if (moving.length === 0) return
      state.pendingStagedAttachments = remaining
      tab.stagedAttachments.push(...moving)
    },

    /** Append staged-file metadata to a conversation tab. */
    addTabStagedAttachments(
      state,
      action: PayloadAction<{ id: string; attachments: StagedAttachment[] }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined || action.payload.attachments.length === 0) return
      tab.stagedAttachments.push(...action.payload.attachments)
    },

    /** Drop a tab's staged-file metadata by client ID. */
    removeTabStagedAttachments(state, action: PayloadAction<{ id: string; clientIds: string[] }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.stagedAttachments = removeStagedAttachmentsById(
        tab.stagedAttachments,
        action.payload.clientIds
      )
    },

    /** Update status for a subset of a tab's staged files. */
    setTabStagedAttachmentStatus(
      state,
      action: PayloadAction<{
        id: string
        clientIds: string[]
        status: StagedAttachmentStatus
        error?: string | null
        errorCode?: ChatAttachmentErrorCode | null
        retryable?: boolean
        errorAction?: ChatAttachmentErrorAction
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.stagedAttachments = applyStagedAttachmentStatus(
        tab.stagedAttachments,
        action.payload.clientIds,
        action.payload.status,
        action.payload.status === 'failed'
          ? {
              error: action.payload.error ?? null,
              errorCode: action.payload.errorCode ?? null,
              retryable: action.payload.retryable ?? true,
              errorAction: action.payload.errorAction ?? 'retry'
            }
          : { error: null, errorCode: null, retryable: false }
      )
    },

    /**
     * Set (or clear, with `null`) a tab's per-conversation model override.
     * Silent no-op when the tab was closed mid-flight.
     */
    setTabModelSelection(
      state,
      action: PayloadAction<{
        id: string
        selection: OrchestratorModelSelection | null
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.modelSelection = action.payload.selection
    },

    /**
     * Set (or clear, with `null`) a tab's per-conversation effort override.
     * Silent no-op when the tab was closed mid-flight.
     */
    setTabEffortSelection(
      state,
      action: PayloadAction<{
        id: string
        selection: OrchestratorEffortSelection | null
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.effortSelection = action.payload.selection
    },

    /**
     * Set a tab's Ask/Agent composer mode. Silent no-op when the tab was
     * closed mid-flight. Persistence lives in the controller — this only
     * updates in-memory tab state.
     */
    setTabComposerMode(
      state,
      action: PayloadAction<{
        id: string
        mode: ChatComposerMode
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.composerMode = action.payload.mode
    },

    // ── Conversation history rail ──────────────────────────────────────

    /** Replace the conversation list (e.g. after a fetch). */
    setAgentOrchestratorConversations(state, action: PayloadAction<ConversationSummary[]>) {
      state.conversations = action.payload
      state.conversationsLoaded = true
    },

    /** Drop a conversation from the list (after a successful delete). */
    removeAgentOrchestratorConversation(state, action: PayloadAction<{ id: string }>) {
      state.conversations = state.conversations.filter((c) => c.id !== action.payload.id)
      const tab = state.tabs[action.payload.id]
      if (tab !== undefined) tab.stagedAttachments = []
    },

    /**
     * Prepend a freshly-created conversation to the rail. Used by the
     * pre-create path that runs at submit time so the rail surfaces the
     * new conversation BEFORE the SSE stream opens. If the id already
     * exists in the list (rare race) it is moved to the head with the new
     * payload.
     */
    prependAgentOrchestratorConversation(state, action: PayloadAction<ConversationSummary>) {
      const idx = state.conversations.findIndex((c) => c.id === action.payload.id)
      if (idx >= 0) state.conversations.splice(idx, 1)
      state.conversations.unshift(action.payload)
    },

    /**
     * Optimistically bump an existing conversation to the top of the rail
     * with a fresh `updated_at`. Dispatched at submit time when the user
     * is continuing an existing conversation, so the rail re-orders
     * immediately instead of waiting for the post-stream refetch. The
     * canonical `updated_at` and `turn_count` are reconciled when
     * `loadAgentConversations` refetches after the stream completes.
     */
    bumpAgentOrchestratorConversationToTop(state, action: PayloadAction<{ id: string }>) {
      const idx = state.conversations.findIndex((c) => c.id === action.payload.id)
      if (idx < 0) return
      const [item] = state.conversations.splice(idx, 1)
      state.conversations.unshift({
        ...item,
        updated_at: new Date().toISOString()
      })
    },

    /**
     * Rewrite a conversation's display title in place. Rename is metadata —
     * it must not reorder the rail or bump `updated_at` (the proxy leaves
     * recency tied to chat activity).
     */
    updateAgentOrchestratorConversationTitle(
      state,
      action: PayloadAction<{ id: string; title: string }>
    ) {
      const item = state.conversations.find((c) => c.id === action.payload.id)
      if (item === undefined) return
      item.title = action.payload.title
    },

    // ── Tab management ─────────────────────────────────────────────────

    /**
     * Add a conversation to `openTabs` and seed its `TabState` if absent.
     * Idempotent: re-opening an already-open tab does not overwrite its
     * draft or in-flight run. Does NOT touch `activeTabId` — callers
     * compose with `setActiveTab` when they want to focus the newly-opened
     * tab.
     *
     * Optional `turns` / `attachments` payloads let the loader thunks seed
     * the persisted-side state in a single dispatch so the chat pane has
     * data to render before the next event loop turn. `hidden` seeds state
     * without adding the conversation to the global Chat tab rail.
     */
    openTab(
      state,
      action: PayloadAction<{
        id: string
        turns?: ConversationRun[]
        attachments?: ConversationAttachment[]
        canContribute?: boolean
        hidden?: boolean
      }>
    ) {
      const { id, turns, attachments, canContribute, hidden = false } = action.payload
      const openIndex = state.openTabs.indexOf(id)
      if (hidden && openIndex >= 0) {
        state.openTabs.splice(openIndex, 1)
        if (state.activeTabId === id) state.activeTabId = null
      } else if (!hidden && openIndex < 0) {
        state.openTabs.push(id)
      }
      if (state.tabs[id] === undefined) {
        state.tabs[id] = makeEmptyTabState()
        state.tabs[id].composerMode = getChatComposerModeForConversation(id)
      }
      if (turns !== undefined) {
        state.tabs[id].turns = mergeInMemoryProgressByStep(state.tabs[id].turns, turns)
        state.tabs[id].turnsLoaded = true
      }
      if (attachments !== undefined) {
        state.tabs[id].attachments = attachments
        state.tabs[id].attachmentsLoaded = true
      }
      if (canContribute !== undefined) {
        state.tabs[id].canContribute = canContribute
      }
    },

    /**
     * Drop a tab from `openTabs` and remove its `TabState`. If the closed
     * tab was active, focus the tab that shifts into its slot (or the one
     * to its left, or null if no tabs remain) so the chat pane never
     * lands on an empty state by accident.
     *
     * Does NOT cancel any in-flight server-side run — that's decision #7
     * in the AXI-3530 plan: close-tab tears down the local SSE consumer
     * but the server-side orchestrator keeps running, and reopening the
     * conversation re-attaches via `/resume`.
     */
    closeTab(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload
      const idx = state.openTabs.indexOf(id)
      if (idx >= 0) {
        state.openTabs.splice(idx, 1)
        if (state.activeTabId === id) {
          state.activeTabId = state.openTabs[idx] ?? state.openTabs[idx - 1] ?? null
        }
      }
      delete state.tabs[id]
    },

    /**
     * Focus a tab. `null` focuses the pending new-chat state (empty
     * composer bound to `pendingDraft`). Refuses to activate a tab that
     * isn't in `openTabs` to prevent rendering against a missing
     * `TabState`; callers must `openTab` first.
     */
    setActiveTab(state, action: PayloadAction<{ id: string | null }>) {
      const { id } = action.payload
      if (id !== null && state.tabs[id] === undefined) return
      state.activeTabId = id
    },

    // ── Per-tab persisted state ────────────────────────────────────────

    /**
     * Replace a tab's transcript after loading the conversation. Silent
     * no-op if the tab was closed between the fetch starting and resolving
     * (defensive against stale responses landing on a closed tab).
     *
     * Carries `progressByStep` forward from the previous turns by array
     * index when the server-returned turn doesn't carry one (the
     * server-loaded turn won't until durable persistence ships).
     */
    setTabTurns(state, action: PayloadAction<{ id: string; turns: ConversationRun[] }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.turns = mergeInMemoryProgressByStep(tab.turns, action.payload.turns)
      tab.turnsLoaded = true
    },

    /**
     * Replace a tab's persisted attachment list, e.g. after
     * `loadActiveAgentConversationAttachments` resolves. Drops on the floor
     * if the tab no longer exists (same staleness guard as `setTabTurns`).
     */
    setTabAttachments(
      state,
      action: PayloadAction<{
        id: string
        attachments: ConversationAttachment[]
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.attachments = action.payload.attachments
      tab.attachmentsLoaded = true
    },

    /**
     * Optimistically append newly-uploaded files to a tab's persisted
     * attachment list once a turn submit has resolved. Lets the documents
     * panel surface this-turn uploads immediately without waiting for the
     * post-stream refetch. Dedup'd by id so a subsequent canonical refetch
     * doesn't create duplicates.
     */
    appendTabAttachments(
      state,
      action: PayloadAction<{
        id: string
        attachments: ConversationAttachment[]
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      const existingIds = new Set(tab.attachments.map((a) => a.id))
      for (const att of action.payload.attachments) {
        if (!existingIds.has(att.id)) tab.attachments.push(att)
      }
    },

    /**
     * Replace the in-flight attachment-preview list for a tab. Pass `[]`
     * to clear. Distinct from the persisted-attachment reducers — these
     * rows live only for the duration of one turn.
     */
    setTabAttachmentPreviews(
      state,
      action: PayloadAction<{ id: string; previews: ConversationAttachment[] }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.liveTurnAttachmentPreviews = action.payload.previews
    },

    /**
     * Append a freshly-completed orchestrator run to a tab's transcript.
     * Called after the stream resolves successfully and the api-service
     * has persisted the run upstream. Silent no-op if the tab was closed
     * between submit and completion (decision #7: closing a tab does not
     * abort the server-side run, so a successful append can still land
     * after the local tab is gone).
     */
    appendTabTurn(state, action: PayloadAction<{ id: string; turn: ConversationRun }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      // Carry committed CoT segments + orchestrator inter-step text from
      // the just-finished live run onto the appended turn so
      // `ChatTurn.extractPast` can re-render them when the user scrolls
      // back. In-memory only — lost on refresh. Skip stamps when there's
      // nothing to preserve so fields stay undefined for past turns and
      // `text`-only plans.
      const segments = tab.run?.liveSegmentsByStep
      const interStepText = tab.run?.interStepTextByStep
      const modelFallback = tab.run?.modelFallback
      const contextCompacted = tab.run?.contextCompacted
      const turn: ConversationRun = { ...action.payload.turn }
      if (segments && Object.keys(segments).length > 0) {
        turn.progressByStep = segments
      }
      if (interStepText && Object.keys(interStepText).length > 0) {
        turn.interStepTextByStep = interStepText
      }
      if (modelFallback !== null && modelFallback !== undefined) {
        turn.modelFallback = modelFallback
      }
      if (contextCompacted !== null && contextCompacted !== undefined) {
        turn.contextCompacted = contextCompacted
      }
      tab.turns.push(turn)
    },

    /**
     * Update a tab's composer draft. Per-tab so switching tabs preserves
     * each tab's typed text (decision #4 in the AXI-3530 plan).
     */
    setTabDraft(state, action: PayloadAction<{ id: string; draft: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.draft = action.payload.draft
    },

    enqueueTabMessage(
      state,
      action: PayloadAction<{ id: string; message: QueuedOrchestratorMessage }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      if (!tab.queuedMessages.some((message) => message.id === action.payload.message.id)) {
        tab.queuedMessages.push(action.payload.message)
      }
    },

    removeTabQueuedMessage(state, action: PayloadAction<{ id: string; messageId: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.queuedMessages = tab.queuedMessages.filter(
        (message) => message.id !== action.payload.messageId
      )
    },

    updateTabQueuedMessage(
      state,
      action: PayloadAction<{ id: string; message: QueuedOrchestratorMessage }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      const index = tab.queuedMessages.findIndex(
        (message) => message.id === action.payload.message.id
      )
      if (index >= 0) tab.queuedMessages[index] = action.payload.message
    },

    setTabQueuedMessages(
      state,
      action: PayloadAction<{
        id: string
        messages: QueuedOrchestratorMessage[]
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.queuedMessages = action.payload.messages
    },

    // ── Per-tab live streaming ─────────────────────────────────────────

    /**
     * Open a fresh in-flight `run` on the named tab. Resets every
     * live-streaming field; the previous run (if any) is overwritten in
     * place. The tab must already be open via `openTab` — silent no-op
     * otherwise.
     */
    startTabRun(
      state,
      action: PayloadAction<{
        id: string
        instruction: string
        runKind?: TabRunState['runKind']
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.run = makeStartedRun(action.payload.instruction, action.payload.runKind)
    },

    /**
     * Flip the in-flight run into "cancelling" mode. Dispatched by the
     * abort thunk before its `POST /agent-orchestrator/cancel` round-trip
     * so the Stop button immediately reflects that the cancel is in
     * flight. The stop / fail / finish reducers clear the flag.
     *
     * No-op when the run has already settled (error / stop notice) — the
     * SSE consumer is gone, so nothing will clear `cancelRequested` and a
     * late Stop click would otherwise leave the button stuck on
     * "Cancelling…".
     */
    cancelTabRun(state, action: PayloadAction<{ id: string }>) {
      const run = state.tabs[action.payload.id]?.run
      if (!isTabRunLive(run)) return
      run.cancelRequested = true
    },

    setTabPlan(state, action: PayloadAction<{ id: string; plan: AgentOrchestratorPlanStep[] }>) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      tab.run.plan = action.payload.plan
    },

    upsertTabStepResult(
      state,
      action: PayloadAction<{
        id: string
        result: AgentOrchestratorStepResult
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      tab.run.stepResults[action.payload.result.stepIndex] = action.payload.result
    },

    /**
     * Append a streamed text chunk to a `text`-kind plan step's summary
     * as it arrives. Driven by `agent_orchestrator_step_text_delta` events
     * fired by the orchestrator pipeline while the planner is mid-prose.
     *
     * The matching `agent_orchestrator_step_complete` event still arrives
     * at end-of-step and overwrites the accumulated string with the
     * canonical full text — defensive against any drift between the
     * concatenated deltas and the final value.
     *
     * Tolerates deltas that arrive BEFORE `agent_orchestrator_step_start`
     * (the orchestrator's eager-emit predicts the slot from plan state
     * and fires deltas as soon as it sees them, which can land before the
     * planner's tool call surfaces the start event). In that case a
     * placeholder row is created with `agent: 'text'`; `step_start` later
     * fills in the real `agent` / `objective` while preserving the
     * accumulated summary.
     */
    appendTabStepDelta(
      state,
      action: PayloadAction<{ id: string; stepIndex: number; delta: string }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      const { stepIndex, delta } = action.payload
      const existing = tab.run.stepResults[stepIndex]
      if (existing) {
        tab.run.stepResults[stepIndex] = {
          ...existing,
          summary: existing.summary + delta
        }
        return
      }
      tab.run.stepResults[stepIndex] = {
        stepIndex,
        agent: 'text',
        objective: '',
        summary: delta,
        completed: false
      }
    },

    upsertTabToolCall(
      state,
      action: PayloadAction<{ id: string; toolCall: LiveOrchestratorToolCall }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      const idx = tab.run.liveToolCalls.findIndex((c) => c.id === action.payload.toolCall.id)
      if (idx >= 0) {
        tab.run.liveToolCalls[idx] = action.payload.toolCall
        return
      }
      tab.run.liveToolCalls.push(action.payload.toolCall)
    },

    /**
     * Append a streamed token to the pending-progress slot for `stepIndex`.
     * Driven by `agent_orchestrator_subagent_progress_delta` events
     * (per-token). The pending text is rendered with in-flight styling
     * and remains mutable until a `commit` or `discard` action settles it.
     *
     * Render-only: never used as the step's canonical summary. The
     * AXI-3586 invariant lives here — the answer body comes from
     * `finalize_answer({ answer })` (or its same-turn text-content
     * fallback captured server-side) via
     * `agent_orchestrator_step_complete`, never from these deltas.
     */
    appendSubagentProgressDelta(
      state,
      action: PayloadAction<{ id: string; stepIndex: number; delta: string }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      const { stepIndex, delta } = action.payload
      if (typeof delta !== 'string' || delta.length === 0) return
      tab.run.pendingProgressByStep[stepIndex] =
        (tab.run.pendingProgressByStep[stepIndex] ?? '') + delta
    },

    /**
     * Append a streamed orchestrator inter-step text delta. The planner
     * LLM's text content block before each `dispatch_subagent` call
     * carries connective prose ("got X, now dispatching Y") that streams
     * token-by-token. Keyed by the step about to be dispatched so the
     * UI renders it above the matching step section.
     */
    appendInterStepTextDelta(
      state,
      action: PayloadAction<{ id: string; stepIndex: number; delta: string }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      const { stepIndex, delta } = action.payload
      if (typeof delta !== 'string' || delta.length === 0) return
      tab.run.interStepTextByStep[stepIndex] =
        (tab.run.interStepTextByStep[stepIndex] ?? '') + delta
    },

    setTabModelFallback(state, action: PayloadAction<{ id: string; fallback: ModelFallbackInfo }>) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      tab.run.modelFallback = action.payload.fallback
    },

    setTabContextCompacted(
      state,
      action: PayloadAction<{ id: string; compacted: ContextCompactedInfo }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      tab.run.contextCompacted = action.payload.compacted
    },

    /**
     * Promote the in-flight pending text for `stepIndex` into a committed
     * transcript segment. Fired on a non-finalize tool_use — the model is
     * done writing a progress note before the next data-gathering call.
     * No-op when pending is empty.
     */
    commitSubagentProgressPending(state, action: PayloadAction<{ id: string; stepIndex: number }>) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      const { stepIndex } = action.payload
      const pending = tab.run.pendingProgressByStep[stepIndex] ?? ''
      if (pending.length === 0) return
      const segments = tab.run.liveSegmentsByStep[stepIndex] ?? []
      segments.push({ kind: 'progress', text: pending })
      tab.run.liveSegmentsByStep[stepIndex] = segments
      delete tab.run.pendingProgressByStep[stepIndex]
    },

    /**
     * Drop in-flight pending text for `stepIndex` without committing the
     * answer as a CoT segment. Fired on finalize tool_use.
     *
     * Critical for query-agent UX: the answer has already been streaming
     * into `pendingProgressByStep` (and the chat body reads it via the
     * fast-path extractor). Clearing pending before
     * `agent_orchestrator_finalized` would blank the answer. Promote the
     * stripped body into `liveFinalSummary` immediately so the visible
     * answer is continuous; later `setTabFinalSummary` may refine cites.
     * Transition prose before `<answer_start>` is kept as a CoT segment.
     *
     * Promotion is scoped to single-direct-query plans (the fast path,
     * mirroring `ChatTurn.singleDirectQueryStepIndex`): only there does
     * the answer body read the streamed pending text. In a multi-step
     * plan a subagent's finalize must NOT promote its narration — doing
     * so flipped the turn to "complete" mid-run and swapped the
     * subagent's text into the answer body (a visible flash), and the
     * planner's later wrap-up deltas then appended onto it. There the
     * finalize-turn text stays a CoT segment instead of being dropped.
     */
    discardSubagentProgressPending(
      state,
      action: PayloadAction<{ id: string; stepIndex: number }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      const { stepIndex } = action.payload
      const pending = tab.run.pendingProgressByStep[stepIndex] ?? ''
      if (pending.length > 0) {
        if (isSingleDirectQueryStep(tab.run.plan, stepIndex)) {
          const markerIdx = pending.toLowerCase().indexOf('<answer_start>')
          if (markerIdx > 0) {
            const preamble = pending.slice(0, markerIdx).trim()
            if (preamble.length > 0) {
              const segments = tab.run.liveSegmentsByStep[stepIndex] ?? []
              segments.push({ kind: 'progress', text: preamble })
              tab.run.liveSegmentsByStep[stepIndex] = segments
            }
          }
          if (tab.run.liveFinalSummary === null || tab.run.liveFinalSummary.length === 0) {
            const promoted = stripAnswerEnvelope(pending).trim()
            if (promoted.length > 0) {
              tab.run.liveFinalSummary = promoted
              tab.run.liveFinalSummaryPromoted = true
            }
          }
        } else {
          // Multi-step subagent finalize: commit the finalize-turn text as
          // an ordinary CoT segment (same as a non-finalize commit) instead
          // of vanishing it — the user was watching it stream, and dropping
          // it flashed. Markers are stripped at render by `displayCotText`.
          const segments = tab.run.liveSegmentsByStep[stepIndex] ?? []
          segments.push({ kind: 'progress', text: pending })
          tab.run.liveSegmentsByStep[stepIndex] = segments
        }
      }
      delete tab.run.pendingProgressByStep[stepIndex]
    },

    /**
     * Record a `wait_for_ingest_job` tool call as in flight so the chat
     * status line can show "Ingesting… (m:ss)" with a live elapsed
     * counter. Dispatched by the SSE handler on `tool_call_start` for the
     * ingest wait tool.
     */
    addTabIngestWait(
      state,
      action: PayloadAction<{
        id: string
        waitId: string
        jobId: string
        startedAtMs: number
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      tab.run.inFlightIngestWaits[action.payload.waitId] = {
        jobId: action.payload.jobId,
        startedAtMs: action.payload.startedAtMs
      }
    },

    /** Clear an in-flight ingest wait once the matching `tool_call_complete` arrives. */
    removeTabIngestWait(state, action: PayloadAction<{ id: string; waitId: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      delete tab.run.inFlightIngestWaits[action.payload.waitId]
    },

    addTabCustomAgentRun(state, action: PayloadAction<{ id: string; run: ChatCustomAgentRun }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.customAgentRuns ??= {}
      tab.customAgentRuns[action.payload.run.runId] = action.payload.run
    },

    removeTabCustomAgentRun(state, action: PayloadAction<{ id: string; runId: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      if (tab.customAgentRuns === undefined) return
      delete tab.customAgentRuns[action.payload.runId]
    },

    addTabCustomAgentBulkBatch(
      state,
      action: PayloadAction<{ id: string; batch: ChatCustomAgentBulkBatch }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.customAgentBulkBatches ??= {}
      tab.customAgentBulkBatches[action.payload.batch.batchId] = action.payload.batch
    },

    removeTabCustomAgentBulkBatch(state, action: PayloadAction<{ id: string; batchId: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab?.customAgentBulkBatches === undefined) return
      delete tab.customAgentBulkBatches[action.payload.batchId]
    },

    /**
     * Record an agent-action approval tool call (e.g. a
     * `propose_cypher_writes` row) as in-flight so the chat sidebar can
     * render the Apply / Reject card and the input-row status line can
     * show "Awaiting your decision…". Multi-statement batches share the
     * parent's `tool_use_id` with a `#<index>` suffix so each row can be
     * applied/rejected independently (index 0 reuses the parent id).
     *
     * Writes to `tab.inFlightAgentActions` (NOT `tab.run.*`) so cards
     * survive the orchestrator stream finishing.
     */
    addTabAgentAction(
      state,
      action: PayloadAction<{
        id: string
        actionId: string
        proposal: InFlightAgentActionProposal
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.inFlightAgentActions[action.payload.actionId] = action.payload.proposal
    },

    /**
     * Merge rows loaded from a capability's pending endpoint (e.g. GET
     * /agent-writes/pending, or a hydrate retry) for one
     * conversation/tab without dropping proposals already parsed from
     * SSE — and without clobbering any locally-tracked terminal state
     * that the user has already produced (Apply / Reject) in this
     * browser session before the resume turn has dispatched.
     */
    mergePendingAgentActionsFromServer(
      state,
      action: PayloadAction<{
        id: string
        entries: Array<{ id: string; proposal: InFlightAgentActionProposal }>
      }>
    ) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      for (const entry of action.payload.entries) {
        const existing = tab.inFlightAgentActions[entry.id]
        if (existing?.terminalState !== undefined) {
          // Preserve the user's local decision; just refresh the
          // server-canonical fields that don't conflict.
          existing.planId = entry.proposal.planId ?? existing.planId
          if (existing.kind === 'cypher_write' && entry.proposal.kind === 'cypher_write') {
            existing.writeId = entry.proposal.writeId ?? existing.writeId
          }
          if (existing.kind === 'tag_write' && entry.proposal.kind === 'tag_write') {
            existing.writeId = entry.proposal.writeId ?? existing.writeId
          }
          if (existing.kind === 'tag_write_policy' && entry.proposal.kind === 'tag_write_policy') {
            existing.writeId = entry.proposal.writeId ?? existing.writeId
          }
          if (
            existing.kind === 'tag_historization' &&
            entry.proposal.kind === 'tag_historization'
          ) {
            existing.writeId = entry.proposal.writeId ?? existing.writeId
          }
          continue
        }
        tab.inFlightAgentActions[entry.id] = entry.proposal
      }
    },

    /**
     * Drop an in-flight agent action by tool_use_id. Searches every open
     * tab — `tool_use_id`s are globally unique so a single hit suffices.
     * Used by the approval panel after a successful apply/reject when
     * the row has no `planId` (defensive fallback for rows that never
     * got their `planId` hydrated).
     */
    removeInFlightAgentAction(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload
      for (const tab of Object.values(state.tabs)) {
        if (tab.inFlightAgentActions[id] !== undefined) {
          delete tab.inFlightAgentActions[id]
          return
        }
      }
    },

    /** Drop an in-flight agent action once the matching `tool_call_complete` arrives or the user resolves it. */
    removeTabAgentAction(state, action: PayloadAction<{ id: string; actionId: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      delete tab.inFlightAgentActions[action.payload.actionId]
    },

    /**
     * Drop EVERY in-flight agent action for a given parent tool-use id
     * on a specific tab. Used on `tool_call_complete` for an approval
     * tool when the server-side persistence failed (validation error,
     * transport error) — in that case no cards were ever actionable so
     * they all need to vacate. On the happy path (`pending: true`
     * envelope) cards persist until the user resolves them per-row.
     */
    removeTabAgentActionsForParent(state, action: PayloadAction<{ id: string; parentId: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      const { parentId } = action.payload
      const prefix = `${parentId}#`
      for (const actionId of Object.keys(tab.inFlightAgentActions)) {
        const proposal = tab.inFlightAgentActions[actionId]
        if (
          actionId === parentId ||
          actionId.startsWith(prefix) ||
          proposal?.batchParentId === parentId
        ) {
          delete tab.inFlightAgentActions[actionId]
        }
      }
    },

    /**
     * Drop EVERY in-flight agent action whose `planId` matches the
     * payload, across every open tab. Used after the synthetic
     * follow-up agent turn for a plan has been dispatched (and the
     * proxy stamped `resume_dispatched_at`), to clear the cards in one
     * step regardless of which tab they live in.
     */
    removeInFlightAgentActionsForPlan(state, action: PayloadAction<{ planId: string }>) {
      const { planId } = action.payload
      for (const tab of Object.values(state.tabs)) {
        for (const id of Object.keys(tab.inFlightAgentActions)) {
          if (tab.inFlightAgentActions[id]?.planId === planId) {
            delete tab.inFlightAgentActions[id]
          }
        }
      }
    },

    /**
     * Update entries belonging to one propose tool-use call with the
     * canonical `planId` / `writeId` from the tool's
     * `tool_call_complete` output. The propose tool now returns
     * synchronously with a `pending: true` payload that carries one
     * `results[]` entry per persisted row; this reducer matches them
     * back to the in-flight entries (keyed by `tool_use_id`) so the
     * per-plan completion check has the data it needs.
     *
     * Searches across every open tab — `tool_use_id`s are globally
     * unique so the caller doesn't need to know which tab owns the row.
     *
     * Does NOT remove any entries — cards persist until the user
     * decides per row.
     */
    hydrateAgentActionProposalsFromToolOutput(
      state,
      action: PayloadAction<{
        parentToolUseId: string
        planId: string
        results: Array<{ toolUseId: string; writeId: string; index?: number }>
      }>
    ) {
      const { parentToolUseId, planId, results } = action.payload
      for (const result of results) {
        for (const tab of Object.values(state.tabs)) {
          const entry = tab.inFlightAgentActions[result.toolUseId]
          if (!entry) continue
          entry.planId = planId
          if (entry.kind === 'cypher_write') entry.writeId = result.writeId
          if (entry.kind === 'tag_write') entry.writeId = result.writeId
          if (entry.batchParentId === undefined) entry.batchParentId = parentToolUseId
          break
        }
      }
    },

    /**
     * Mark a per-row agent-action proposal as locally settled (applied
     * or rejected) without removing it from `inFlightAgentActions`. The
     * card stays visible (in a "settled" presentation) until the
     * synthetic follow-up agent turn for the row's `planId` is
     * dispatched, at which point `removeInFlightAgentActionsForPlan`
     * clears every row of the plan together.
     *
     * `settledMeta` is capability-supplied data merged onto the entry
     * (e.g. cypher applied-counts or rejection reason) so the slice
     * stays agnostic to any single capability's shape.
     *
     * Searches across tabs — `tool_use_id`s are globally unique.
     */
    markAgentActionSettled(
      state,
      action: PayloadAction<{
        id: string
        terminal: AgentActionTerminalState
        settledMeta?: Record<string, unknown>
      }>
    ) {
      for (const tab of Object.values(state.tabs)) {
        const entry = tab.inFlightAgentActions[action.payload.id]
        if (!entry) continue
        entry.terminalState = action.payload.terminal
        if (action.payload.settledMeta) Object.assign(entry, action.payload.settledMeta)
        return
      }
    },

    /**
     * Capture the planner's `agent_orchestrator_finalized` summary while the
     * stream is still open. Skips the write when the canonical summary is
     * character-identical to what's already displayed (e.g. the promoted
     * fast-path answer) so the answer body doesn't churn at finalize.
     */
    setTabFinalSummary(state, action: PayloadAction<{ id: string; summary: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      tab.run.liveFinalSummaryPromoted = false
      if (tab.run.liveFinalSummary === action.payload.summary) return
      tab.run.liveFinalSummary = action.payload.summary
    },

    /**
     * Append a streamed text chunk to the live final-summary as it
     * arrives. Driven by `agent_orchestrator_final_summary_delta` events
     * emitted by the orchestrator pipeline while the planner is composing
     * its synthesized wrap-up. The terminal `agent_orchestrator_finalized`
     * event still fires once the prose is complete and overwrites the
     * accumulated string with the canonical value (defensive against
     * drift between the concatenated deltas and the final summary).
     */
    appendTabFinalSummaryDelta(state, action: PayloadAction<{ id: string; delta: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      if (tab.run.liveFinalSummaryPromoted) {
        // The displayed summary is the client-promoted streamed answer, not
        // server text — a synthesis turn is now streaming the real wrap-up.
        // Restart from the delta instead of appending after the promotion
        // (which duplicated the answer above the synthesis).
        tab.run.liveFinalSummary = action.payload.delta
        tab.run.liveFinalSummaryPromoted = false
        return
      }
      tab.run.liveFinalSummary = (tab.run.liveFinalSummary ?? '') + action.payload.delta
    },

    /**
     * The in-flight run failed. Keeps `tab.run` non-null so the error
     * banner stays visible inside the chat pane until the user starts
     * another run or dismisses it — symmetric with the red-dot indicator
     * on the tab strip for backgrounded failures (decision #8).
     */
    failTabRun(state, action: PayloadAction<{ id: string; error: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab?.run === null || tab?.run === undefined) return
      tab.run.streamError = action.payload.error
      tab.run.inFlightIngestWaits = {}
      // Cypher-write proposals persist across run failure — the propose
      // tool persists rows server-side before the run errors out, and
      // the user can still act on them via the apply/reject endpoints.
      // The turn never completed — drop the optimistic preview rows so
      // the documents panel doesn't show files that were never persisted.
      tab.liveTurnAttachmentPreviews = []
      tab.run.cancelRequested = false
    },

    /**
     * Clear all live-streaming fields once the turn has been promoted into
     * `tab.turns` (via `appendTabTurn`). Used by a completed turn and by a
     * stopped one — both keep their work as a turn, so there is nothing
     * left for `tab.run` to hold. Distinct from `failTabRun`, whose error
     * banner has no turn to live on.
     */
    finishTabRun(state, action: PayloadAction<{ id: string }>) {
      const tab = state.tabs[action.payload.id]
      if (tab === undefined) return
      tab.run = null
      // Canonical attachment rows have arrived via the post-stream
      // refetch in the orchestrator thunk; drop the optimistic previews
      // so they don't render alongside their persisted twins.
      tab.liveTurnAttachmentPreviews = []
    }
  }
})

export const {
  addPendingStagedAttachments,
  addTabAgentAction,
  addTabIngestWait,
  addTabStagedAttachments,
  appendTabAttachments,
  appendSubagentProgressDelta,
  appendTabFinalSummaryDelta,
  appendTabStepDelta,
  appendTabTurn,
  addTabCustomAgentBulkBatch,
  addTabCustomAgentRun,
  bumpAgentOrchestratorConversationToTop,
  cancelTabRun,
  closeTab,
  appendInterStepTextDelta,
  setTabModelFallback,
  setTabContextCompacted,
  commitSubagentProgressPending,
  discardSubagentProgressPending,
  enqueueTabMessage,
  failTabRun,
  finishTabRun,
  hydrateAgentActionProposalsFromToolOutput,
  markAgentActionSettled,
  mergePendingAgentActionsFromServer,
  openTab,
  prependAgentOrchestratorConversation,
  removeAgentOrchestratorConversation,
  removeInFlightAgentAction,
  removeInFlightAgentActionsForPlan,
  removePendingStagedAttachments,
  removeTabAgentAction,
  removeTabAgentActionsForParent,
  removeTabIngestWait,
  removeTabCustomAgentBulkBatch,
  removeTabCustomAgentRun,
  removeTabQueuedMessage,
  removeTabStagedAttachments,
  setActiveTab,
  setAgentOrchestratorConversations,
  setPendingDraft,
  setPendingEffortSelection,
  setPendingComposerMode,
  setPendingModelSelection,
  setPendingStagedAttachmentStatus,
  setTabAttachmentPreviews,
  setTabAttachments,
  setTabDraft,
  setTabEffortSelection,
  setTabComposerMode,
  setTabModelSelection,
  setTabQueuedMessages,
  setTabFinalSummary,
  setTabPlan,
  setTabStagedAttachmentStatus,
  setTabTurns,
  startTabRun,
  transferPendingStagedAttachments,
  upsertTabStepResult,
  upsertTabToolCall,
  updateAgentOrchestratorConversationTitle,
  updateTabQueuedMessage
} = agentOrchestratorSlice.actions

// ── Selectors ─────────────────────────────────────────────────────────

interface RootStateLike {
  agentOrchestrator: AgentOrchestratorState
}

/**
 * The currently-focused tab's state, or `null` when the user is on the
 * pending new-chat tab (`activeTabId === null`). Components that render
 * the chat pane should read off this selector rather than indexing into
 * `tabs` themselves so the pending-state path stays consistent.
 */
export function selectActiveTab(state: RootStateLike): TabState | null {
  const id = state.agentOrchestrator.activeTabId
  if (id === null) return null
  return state.agentOrchestrator.tabs[id] ?? null
}

/** Look up a tab by conversation id, or `undefined` if it isn't open. */
export function selectTab(state: RootStateLike, id: string): TabState | undefined {
  return state.agentOrchestrator.tabs[id]
}

const EMPTY_STAGED_ATTACHMENTS: StagedAttachment[] = []

/** Staged composer files for the pending new-chat tab. */
export function selectPendingStagedAttachments(state: RootStateLike): StagedAttachment[] {
  return state.agentOrchestrator.pendingStagedAttachments
}

/** Staged composer files for a conversation tab, or `[]` if it isn't open. */
export function selectTabStagedAttachments(state: RootStateLike, id: string): StagedAttachment[] {
  return state.agentOrchestrator.tabs[id]?.stagedAttachments ?? EMPTY_STAGED_ATTACHMENTS
}

/**
 * Staged composer files for the focused tab, or pending new-chat files when
 * `activeTabId === null`.
 */
export function selectActiveStagedAttachments(state: RootStateLike): StagedAttachment[] {
  const id = state.agentOrchestrator.activeTabId
  if (id === null) return state.agentOrchestrator.pendingStagedAttachments
  return state.agentOrchestrator.tabs[id]?.stagedAttachments ?? EMPTY_STAGED_ATTACHMENTS
}

/** `true` when any of the given staged files is mid-submit. */
export function hasSendingStagedAttachments(items: StagedAttachment[]): boolean {
  return items.some((item) => item.status === 'sending')
}

/** `true` when local or server policy rejected a file until it is removed. */
export function hasNonRetryableStagedAttachmentFailures(items: StagedAttachment[]): boolean {
  return items.some((item) => item.status === 'failed' && !item.retryable)
}

/**
 * `true` while an SSE consumer is still live for this tab run.
 *
 * `failTabRun` keeps `tab.run` non-null so the error banner stays visible,
 * but that run is settled — the composer must show Continue (not
 * Stop/Cancelling) and the tab strip must show the error/idle indicator
 * rather than a streaming spinner.
 */
export function isTabRunLive(run: TabRunState | null | undefined): run is TabRunState {
  if (run == null) return false
  return run.streamError == null
}

/** `true` if the tab for `id` has a live (not yet settled) in-flight run. */
export function selectIsTabStreaming(state: RootStateLike, id: string): boolean {
  return isTabRunLive(state.agentOrchestrator.tabs[id]?.run)
}

/**
 * All open tab ids that are currently streaming, in `openTabs` order.
 * Drives the per-tab spinner / dot indicators on the tab strip.
 */
export function selectStreamingTabIds(state: RootStateLike): string[] {
  const out: string[] = []
  for (const id of state.agentOrchestrator.openTabs) {
    if (isTabRunLive(state.agentOrchestrator.tabs[id]?.run)) out.push(id)
  }
  return out
}

/** `true` if any open tab has an in-flight run. */
export function selectIsAnyTabStreaming(state: RootStateLike): boolean {
  for (const id of state.agentOrchestrator.openTabs) {
    if (isTabRunLive(state.agentOrchestrator.tabs[id]?.run)) return true
  }
  return false
}

export type AgentOrchestratorStepKind = OrchestratedStepKind

// Re-export the planning types from the wire package so UI consumers can
// import everything they need from the state-package barrel.
export type {
  AgentOrchestratorPlanStep,
  AgentOrchestratorStepResult,
  OrchestratedStepKind
} from '@atbash-labs/ontology-agent-wire'

export default agentOrchestratorSlice.reducer
